// Account fetching and normalisation for Drip vaults.
//
// The program has two account layouts: `VaultState`/`UserPosition` (legacy
// kySOL) and `AssetVault`/`AssetUserPosition` (generic asset vaults). These
// functions read either and return one normalised shape, so every hook — and
// both the single-vault and all-vaults variants — shares one mapping.
//
// Pure functions rather than hooks: no React, so they can be unit-tested and
// composed by callers that need N vaults in one pass.

import { AccountMeta, Connection, PublicKey } from "@solana/web3.js";
import {
  getAccount,
  getAssociatedTokenAddress,
  getMint,
  TokenAccountNotFoundError,
  TokenInvalidAccountOwnerError,
} from "@solana/spl-token";
import { Program } from "@coral-xyz/anchor";
import { DripVaultDescriptor } from "../../constants/dripVaults";
import { deriveDripPdas } from "./dripPdas";
import { bookedSharesRaw, isClaimShort, withdrawableSharesRaw } from "./dripClaim";
import { computeWithdrawableRaw, withdrawalFeePercent } from "./dripValue";
import { MINT_TO_TOKEN, YIELD_TOKEN_IMAGES } from "../../constants/dripTokens";

export const SOL_MINT = "So11111111111111111111111111111111111111112";

/** On-chain shares, principal and rate are all 1e9-scaled. */
export const DRIP_SCALE = 1e9;

/**
 * Global stats for one Drip vault, normalised across the two account layouts.
 *
 * `VaultState` (legacy kySOL) names its fields kySolStaked/kySolRate/kySolMint;
 * `AssetVault` names them staked/rate/depositMint. Consumers see one shape.
 */
export interface DripVaultState {
  vaultId: string;
  symbol: string;
  /** Principal held, in deposit-token units. */
  staked: number;
  stakedUsd: number | null;
  /**
   * SOL value of `staked`: equal to `staked` for the legacy kySOL vault
   * (1 kySOL ~= 1 SOL), or `staked * rate` for asset vaults. Callers that
   * need a vault's SOL-denominated principal should read this rather than
   * re-deriving it by branching on vault kind themselves.
   */
  stakedSol: number;
  /** Vault value in SOL terms. */
  totalAssets: number;
  shareSupply: number | null;
  /** SOL per deposit token. */
  rate: number;
  /** Raw on-chain fee, in millionths. */
  withdrawalFee: number;
  /** Fee as a display percentage. */
  withdrawalFeePct: number;
  paused: boolean;
  pendingSharesLatest: number;
  pendingSharesPrevious: number;
  latestPendingEpoch: number;
  admin: string;
  swapper: string;
  feeRecipient: string;
  shareMint: string;
  depositMint: string;
  /** Legacy kySOL vault only: the Kyros restaking vault it reads its rate from. */
  kyrosVault?: string;
  /** Legacy kySOL vault only: the Jito stake pool it reads its rate from. */
  jitoStakePool?: string;
  /** Asset vaults only: the pinned SPL stake pool acting as rate oracle. */
  stakePool?: string;
}

export interface DripPosition {
  vaultId: string;
  symbol: string;
  depositMint: string;
  shares: number;
  /**
   * Raw shares the wallet can actually redeem: the booked position clamped to
   * the share tokens it still holds. NOT the position's own share count — see
   * `bookedSharesRaw` for that.
   */
  sharesRaw: number;
  /** Raw split, kept because the warm-up check compares against exactly zero. */
  activeSharesRaw: number;
  pendingSharesRaw: number;
  /** Redeemable deposit tokens, using the program's own formula. */
  tokenValue: number;
  /**
   * True when the share-mint supply or the wallet's share balance could not be
   * read, so `tokenValue` is NOT a balance. Callers must render this as
   * unavailable — never as a zero balance, never as "no position", never as a
   * lost claim — and must not build a transaction from it.
   */
  isValueUnavailable: boolean;
  tokenValueUsd: number | null;
  rate: number;
  /** Stringified bigints so callers can recompute share amounts exactly. */
  stakedRaw: string;
  shareSupplyRaw: string;
  /** What the position PDA books: active plus pending shares. */
  bookedSharesRaw: string;
  /** Share-mint balance in the owner's ATA, the account `redeem` burns from. */
  heldSharesRaw: string;
  /** min(booked, held): what `redeem` can actually be built for. */
  withdrawableSharesRaw: string;
  /**
   * True when the IOU no longer covers the booked position. Unlike
   * `isValueUnavailable` this is permanent, so the two must not share a message.
   */
  isClaimShort: boolean;
  rewardTokenMint: string;
  rewardTokenName: string;
  rewardTokenImage: string;
  pendingShares: number;
  lastDepositEpoch: number;
  nextEpochEstimateSeconds: number | null;
  isWarmingUp: boolean;
  withdrawalFee: number;
  withdrawalFeePct: number;
  feeRecipient: string;
}

/**
 * SOL value of a deposit-token amount held in one vault.
 *
 * Asset vaults expose an exact SOL-per-token rate; the legacy kySOL vault keeps
 * the app's long-standing 1 kySOL ~= 1 SOL assumption. That assumption lives in
 * this one expression — read it through here rather than re-branching on
 * `vault.kind` at each call site.
 */
export const solValueOf = (
  vault: Pick<DripVaultDescriptor, "kind">,
  tokenAmount: number,
  rate: number
): number => (vault.kind === "kysol" ? tokenAmount : tokenAmount * rate);

/** Best-effort SOL spot price. Fetched once per pass by multi-vault callers. */
export const fetchSolPrice = async (): Promise<number | null> => {
  try {
    const res = await fetch(`https://lite-api.jup.ag/price/v3?ids=${SOL_MINT}`);
    const data = await res.json();
    return data?.[SOL_MINT]?.usdPrice ?? null;
  } catch {
    return null;
  }
};

/** Builds an Anchor program pinned to the env-configured Drip address. */
export const dripProgramFor = (
  idl: unknown,
  dripProgramAddress: string,
  connection: Connection
): Program =>
  new Program({ ...(idl as any), address: dripProgramAddress } as any, { connection });

/**
 * The rate-oracle accounts a vault's settling instructions must pass, in the order
 * the program appends them.
 *
 * Every deposit and redeem now settles accrued yield before pricing the operation,
 * which needs the live rate. So all four take their vault's pinned oracle as
 * trailing account(s):
 *
 *                  legacy ('kysol')                asset ('asset')
 *   appended       jito_stake_pool, kyros_vault    stake_pool
 *
 * Read off the vault account rather than pinned in config: the program constrains
 * each against `vault_state`, so a stale local copy fails outright with
 * InvalidAccountData (6011) instead of doing something subtle. They are appended
 * LAST on purpose — the pre-upgrade program ignores trailing accounts it does not
 * know, so this client is safe to ship BEFORE the program upgrade lands, and must be.
 */
export const fetchDripOracleAccounts = async (
  program: Program,
  vault: DripVaultDescriptor,
  programId: PublicKey
): Promise<AccountMeta[]> => {
  const { vaultState: vaultStatePda } = deriveDripPdas(vault, programId);
  const data = await fetchVaultAccount(program, vault, vaultStatePda);

  if (!data) {
    throw new Error(
      `Could not read the Drip vault account for "${vault.id}" to resolve its rate oracle.`
    );
  }

  const readOnly = (pubkey: PublicKey): AccountMeta => ({
    pubkey,
    isSigner: false,
    isWritable: false,
  });

  return vault.kind === "kysol"
    ? [readOnly(data.jitoStakePool), readOnly(data.kyrosVault)]
    : [readOnly(data.stakePool)];
};

/** Reads the vault account for either family. Null when it does not exist. */
const fetchVaultAccount = async (
  program: Program,
  vault: DripVaultDescriptor,
  vaultStatePda: PublicKey
): Promise<any | null> =>
  vault.kind === "kysol"
    ? // @ts-ignore - account names resolve at runtime from the IDL
      program.account.vaultState.fetch(vaultStatePda).catch(() => null)
    : // @ts-ignore
      program.account.assetVault.fetch(vaultStatePda).catch(() => null);

/**
 * Raw share-token balance in the owner's ATA, or null when the read failed.
 *
 * The ATA specifically, because that is the account `redeem` burns from: a
 * balance parked in some other token account would not make the instruction
 * succeed.
 *
 * A missing account is a genuine zero — the mainnet case is a rent-reclaim tool
 * that burned the balance and closed the account. Every other error returns
 * null so the caller reports it as unavailable rather than as a destroyed claim.
 */
const fetchHeldSharesRaw = async (
  connection: Connection,
  shareMint: PublicKey,
  owner: PublicKey
): Promise<bigint | null> => {
  try {
    const ata = await getAssociatedTokenAddress(shareMint, owner, true);
    const account = await getAccount(connection, ata);
    return BigInt(account.amount.toString());
  } catch (err) {
    if (
      err instanceof TokenAccountNotFoundError ||
      err instanceof TokenInvalidAccountOwnerError
    ) {
      return BigInt(0);
    }
    return null;
  }
};

/** Reads the position account for either family. Null when it does not exist. */
const fetchPositionAccount = async (
  program: Program,
  vault: DripVaultDescriptor,
  userPositionPda: PublicKey
): Promise<any | null> =>
  vault.kind === "kysol"
    ? // @ts-ignore
      program.account.userPosition.fetch(userPositionPda).catch(() => null)
    : // @ts-ignore
      program.account.assetUserPosition.fetch(userPositionPda).catch(() => null);

/**
 * Normalised state for one vault.
 *
 * `solPrice` is passed in rather than fetched here so a caller reading N vaults
 * makes one price request, not N.
 */
export const fetchDripVaultState = async (
  program: Program,
  connection: Connection,
  vault: DripVaultDescriptor,
  programId: PublicKey,
  solPrice: number | null
): Promise<DripVaultState | null> => {
  const { vaultState: vaultStatePda } = deriveDripPdas(vault, programId);
  const data = await fetchVaultAccount(program, vault, vaultStatePda);
  if (!data) return null;

  const isLegacy = vault.kind === "kysol";
  const shareMint = data.shareMint.toBase58();

  const shareSupply = await getMint(connection, new PublicKey(shareMint))
    .then((info) => Number(info.supply) / Math.pow(10, info.decimals))
    .catch(() => null);

  const staked = Number(isLegacy ? data.kySolStaked : data.staked) / DRIP_SCALE;
  const rate = Number(isLegacy ? data.kySolRate : data.rate) / DRIP_SCALE;
  const solValue = solValueOf(vault, staked, rate);
  const withdrawalFee = Number(data.withdrawalFee);

  return {
    vaultId: vault.id,
    symbol: vault.symbol,
    staked,
    stakedUsd: solPrice != null ? solValue * solPrice : null,
    stakedSol: solValue,
    totalAssets: Number(data.totalAssets) / DRIP_SCALE,
    shareSupply,
    rate,
    withdrawalFee,
    withdrawalFeePct: withdrawalFeePercent(withdrawalFee),
    paused: Boolean(data.paused),
    pendingSharesLatest: Number(data.pendingSharesLatest) / DRIP_SCALE,
    pendingSharesPrevious: Number(data.pendingSharesPrevious) / DRIP_SCALE,
    latestPendingEpoch: Number(data.latestPendingEpoch),
    admin: data.admin.toBase58(),
    swapper: data.swapper.toBase58(),
    feeRecipient: data.feeRecipient.toBase58(),
    shareMint,
    depositMint: (isLegacy ? data.kySolMint : data.depositMint).toBase58(),
    kyrosVault: isLegacy ? data.kyrosVault.toBase58() : undefined,
    jitoStakePool: isLegacy ? data.jitoStakePool.toBase58() : undefined,
    stakePool: isLegacy ? undefined : data.stakePool.toBase58(),
  };
};

/**
 * One wallet's position in one vault, or null when there is none.
 *
 * Returns everything derivable from the two accounts plus the share supply.
 * `tokenValueUsd`, `nextEpochEstimateSeconds` and `isWarmingUp` are left at
 * their neutral values — they need a price fetch and an epoch lookup, which
 * `useDripPosition` layers on for the detail view. List views skip them.
 */
export const fetchDripPositionCore = async (
  program: Program,
  connection: Connection,
  vault: DripVaultDescriptor,
  programId: PublicKey,
  owner: PublicKey
): Promise<DripPosition | null> => {
  const { vaultState: vaultStatePda, userPosition: userPositionPda, shareMint } =
    deriveDripPdas(vault, programId, owner);
  if (!userPositionPda) return null;

  const [positionData, vaultData] = await Promise.all([
    fetchPositionAccount(program, vault, userPositionPda),
    fetchVaultAccount(program, vault, vaultStatePda),
  ]);
  if (!positionData || !vaultData) return null;

  const isLegacy = vault.kind === "kysol";
  // Unclamped: the warm-up check compares both against exactly zero.
  const activeSharesRaw = Number(positionData.shares);
  const pendingSharesRaw = Number(positionData.pendingShares ?? 0);
  const lastDepositEpoch = Number(positionData.lastDepositEpoch);

  const stakedRaw = BigInt(
    (isLegacy ? vaultData.kySolStaked : vaultData.staked).toString()
  );
  // A failed read must stay distinguishable from a genuine zero: presenting it
  // as the balance would tell a funded wallet it holds nothing, and an
  // unreadable share balance is not a burned one.
  const [shareSupplyOrNull, heldSharesOrNull] = await Promise.all([
    getMint(connection, shareMint)
      .then((info) => info.supply)
      .catch(() => null),
    fetchHeldSharesRaw(connection, shareMint, owner),
  ]);
  const isValueUnavailable = shareSupplyOrNull === null || heldSharesOrNull === null;
  // Kept at 0 on failure so computeSharesForTokensRaw also returns 0 and no
  // transaction can be built from an unavailable read.
  const shareSupplyRaw = shareSupplyOrNull ?? BigInt(0);
  const heldSharesRaw = heldSharesOrNull ?? BigInt(0);

  // Off the raw account values rather than the Number copies above, so the
  // clamp stays exact past Number.MAX_SAFE_INTEGER.
  const bookedRaw = bookedSharesRaw(
    BigInt(positionData.shares.toString()),
    BigInt((positionData.pendingShares ?? 0).toString())
  );
  // An unavailable read withdraws nothing and claims nothing about the IOU:
  // a 0 here means "unknown", not "burned".
  const withdrawableRaw = isValueUnavailable
    ? BigInt(0)
    : withdrawableSharesRaw(bookedRaw, heldSharesRaw);
  const claimShort = !isValueUnavailable && isClaimShort(bookedRaw, heldSharesRaw);

  // Everything below derives from the clamp, so a consumer that never heard of
  // the IOU is capped anyway.
  const sharesRaw = Number(withdrawableRaw);

  // The program pays out shares * staked / share_supply and never reads the
  // rate, so the share supply is required for an accurate figure.
  const tokenValue =
    Number(computeWithdrawableRaw(withdrawableRaw, stakedRaw, shareSupplyRaw)) /
    DRIP_SCALE;

  const rewardTokenMint = positionData.rewardTokenMint.toBase58();
  const rewardTokenName = MINT_TO_TOKEN[rewardTokenMint] || "Unknown";
  const withdrawalFee = Number(vaultData.withdrawalFee);

  return {
    vaultId: vault.id,
    symbol: vault.symbol,
    depositMint: vault.depositMint,
    shares: sharesRaw / DRIP_SCALE,
    sharesRaw,
    activeSharesRaw,
    pendingSharesRaw,
    tokenValue,
    isValueUnavailable,
    tokenValueUsd: null,
    rate: Number(isLegacy ? vaultData.kySolRate : vaultData.rate) / DRIP_SCALE,
    stakedRaw: stakedRaw.toString(),
    shareSupplyRaw: shareSupplyRaw.toString(),
    bookedSharesRaw: bookedRaw.toString(),
    heldSharesRaw: heldSharesRaw.toString(),
    withdrawableSharesRaw: withdrawableRaw.toString(),
    isClaimShort: claimShort,
    rewardTokenMint,
    rewardTokenName,
    rewardTokenImage: YIELD_TOKEN_IMAGES[rewardTokenName as keyof typeof YIELD_TOKEN_IMAGES] || "",
    pendingShares: pendingSharesRaw / DRIP_SCALE,
    lastDepositEpoch,
    nextEpochEstimateSeconds: null,
    isWarmingUp: false,
    withdrawalFee,
    withdrawalFeePct: withdrawalFeePercent(withdrawalFee),
    feeRecipient: vaultData.feeRecipient.toBase58(),
  };
};
